import { createFileRoute, getRouteApi, useParams } from '@tanstack/react-router';
import type { Row } from '@tanstack/react-table';
import { zodValidator } from '@tanstack/zod-adapter';
import { useState } from 'react';
import { DataTable, DataTablePagination } from '@/components/DataTable';
import { ConfirmActionDialog } from '@/components/Dialog/ConfirmActionDialog';
import { FillScreen, HStack, VStack } from '@/components/Layout';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { defaultNS, useTranslation } from '@/lib/i18n';
import EditPlanDialog from '../-components/EditPlanDialog';
import { PlanHeader } from '../-components/Header';
import PlanHeaderSkeleton from '../-components/HeaderSkeleton';
import { useCompleteDepartmentRestructuring, useDepartmentRestructuringDetailQuery } from '../-query';
import { TransfersFilterBar } from './-components/TransfersFilterBar';
import { useTransfersTable } from './-hooks/useTransfersTable';
import type { TransferTableRow } from './-query/selector';
import { TransfersSearchSchema } from './-zod-schema';
const transfersRouteApi = getRouteApi('/_staff/organization/plan/$id/transfers/');
export const Route = createFileRoute('/_staff/organization/plan/$id/transfers/')({
  component: TransfersTab,
  validateSearch: zodValidator(TransfersSearchSchema)
});
function TransfersTab() {
  const {
    id
  } = useParams({
    from: '/_staff/organization/plan/$id/transfers/'
  });
  const search = Route.useSearch();
  const {
    data: planDetail,
    isLoading
  } = useDepartmentRestructuringDetailQuery(id);
  const completeMutation = useCompleteDepartmentRestructuring(id);
  const {
    table,
    displayState,
    count
  } = useTransfersTable();
  const navigate = transfersRouteApi.useNavigate();
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.organization.plan.detailPage'
  });
  const tc = useCommonTranslation();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isApproveConfirmOpen, setIsApproveConfirmOpen] = useState(false);
  const handleEdit = () => setIsEditOpen(true);
  const handleApprove = () => setIsApproveConfirmOpen(true);
  const handleConfirmApprove = () => {
    if (!planDetail) return;
    setIsApproveConfirmOpen(false);
    completeMutation.mutate({
      version: planDetail.version
    });
  };
  const handleRowClick = (row: Row<TransferTableRow>) => {
    navigate({
      to: '/person/$id',
      params: {
        id: row.original.person.id
      }
    });
  };
  if (isLoading || !planDetail) {
    return <VStack className="gap-4">
        <PlanHeaderSkeleton />
        <div className="px-12 py-5">
          <div className="h-64 rounded bg-neutral-muted-on-fill/10 animate-pulse" />
        </div>
      </VStack>;
  }
  return <VStack className="gap-4">
      <PlanHeader data={planDetail} onEdit={handleEdit} onApprove={handleApprove} isApproving={completeMutation.isPending} />

      <FillScreen className="flex flex-col gap-4 px-12 py-5">
        <HStack className="justify-between items-center p-2 rounded bg-neutral-fill">
          <TransfersFilterBar restructuringId={id} filterState={{
          departmentIdIn: search.departmentIdIn,
          positionIdIn: search.positionIdIn
        }} onFilterChange={state => navigate({
          search: prev => ({
            ...prev,
            ...state,
            pageIndex: 0
          }),
          replace: true
        })} />
        </HStack>
        <div className="overflow-auto flex-1 min-h-0">
          <DataTable table={table} displayState={displayState} onRowClick={handleRowClick} isHoverable />
        </div>
        <DataTablePagination table={table} totalCount={count} />
      </FillScreen>

      <EditPlanDialog isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} data={planDetail} />
      <ConfirmActionDialog isOpen={isApproveConfirmOpen} onClose={() => setIsApproveConfirmOpen(false)} onConfirm={handleConfirmApprove} title={{
      resource: planDetail.name,
      action: tc('action.approve')
    }} description={t('completeDialog.description')} okLabel={tc('action.approve')} />
    </VStack>;
}